import { CREATURE_TYPES, type CreatureType, type FaunaRules } from "@eldrun/content";
import { deriveSeed, hash32, toUnitFloat } from "@eldrun/shared";
import { BIOME_NAMES, classifyBiome } from "./biomes.ts";
import { sampleGradient, sampleHeight, type Heightfield } from "./heightfield.ts";
import { WORLD_HALF, WORLD_SIZE } from "./params.ts";
import { samplePlanGrid } from "./planner.ts";
import { PoiFootprints } from "./poi-objects.ts";
import type { ResourcePlan } from "./resources.ts";

/** Zona de fauna: o servidor mantém nela um bando do tipo enquanto houver jogador por perto. */
export interface FaunaZone {
  id: number;
  type: number;
  x: number;
  z: number;
  radius: number;
  /** Teto do bando (dentro de `flock` do tipo). */
  cap: number;
}

/** Célula da grade de zonas (8 × 8 na ilha): no máximo uma zona por tipo e célula. */
export const FAUNA_CELL = 256;
const CELLS = WORLD_SIZE / FAUNA_CELL;
const MARGIN = 0.2;
/** Pontos tentados por célula: a zona fica no primeiro que cair no bioma certo. */
const ATTEMPTS = 4;

/**
 * Zonas de fauna por bioma, a partir da seed (doc 06, Fauna neutra): capivara no mangue e na
 * beira d'água, tatu no cerrado, ema no cerrado aberto. Decisão por célula com hash sem estado,
 * então o resultado é o mesmo em qualquer ordem e em qualquer processo.
 */
export function faunaZones(plan: ResourcePlan, heightfield: Heightfield): FaunaZone[] {
  const zones: FaunaZone[] = [];
  // Bichos não fazem toca dentro de acampamento, ruína ou santuário (pegadas dos POIs).
  const footprints = new PoiFootprints(plan.pois ?? []);
  const gradient = { x: 0, z: 0 };
  for (const type of CREATURE_TYPES) {
    if (!type.fauna) continue;
    const seed = deriveSeed(plan.seed, "fauna", type.index);
    for (let j = 0; j < CELLS; j++) {
      for (let i = 0; i < CELLS; i++) {
        const u = (k: number) => toUnitFloat(hash32(seed, i, j, k));
        const zone = evaluate(type, plan, heightfield, gradient, i, j, u);
        if (zone && !footprints.inside(zone.x, zone.z)) zones.push(zone);
      }
    }
  }
  return zones;
}

function evaluate(
  type: CreatureType,
  plan: ResourcePlan,
  heightfield: Heightfield,
  gradient: { x: number; z: number },
  i: number,
  j: number,
  u: (k: number) => number,
): FaunaZone | undefined {
  const fauna = type.fauna as FaunaRules;
  if (u(0) >= fauna.zone.density) return undefined;
  for (let a = 0; a < ATTEMPTS; a++) {
    const point = candidate(type, plan, heightfield, gradient, i, j, u(1 + 3 * a), u(2 + 3 * a), u(3 + 3 * a));
    if (!point) continue;
    const [min, max] = fauna.flock;
    return { id: type.index * 256 + j * CELLS + i, type: type.index, x: point.x, z: point.z, radius: fauna.zone.radius, cap: min + Math.floor(u(20) * (max - min + 1)) };
  }
  return undefined;
}

/** Um ponto da célula serve para a zona? (terra, declive, distância do mar e peso do bioma) */
function candidate(
  type: CreatureType,
  plan: ResourcePlan,
  heightfield: Heightfield,
  gradient: { x: number; z: number },
  i: number,
  j: number,
  ux: number,
  uz: number,
  ubiome: number,
): { x: number; z: number } | undefined {
  const zone = (type.fauna as FaunaRules).zone;
  const margin = FAUNA_CELL * MARGIN;
  const x = -WORLD_HALF + i * FAUNA_CELL + margin + (FAUNA_CELL - 2 * margin) * ux;
  const z = -WORLD_HALF + j * FAUNA_CELL + margin + (FAUNA_CELL - 2 * margin) * uz;
  const y = sampleHeight(heightfield, x, z);
  if (y < 0.5) return undefined;
  const g = sampleGradient(heightfield, x, z, gradient);
  const slope = Math.sqrt(g.x * g.x + g.z * g.z);
  if (slope > zone.maxSlope) return undefined;
  const coastDistance = samplePlanGrid(plan.coastDistance, x, z);
  if (zone.maxCoastDistance !== undefined && coastDistance > zone.maxCoastDistance) return undefined;
  const biome = classifyBiome({
    y,
    moisture: samplePlanGrid(plan.moisture, x, z),
    slope,
    coastDistance,
    landDistance: samplePlanGrid(plan.landDistance, x, z),
  });
  const weight = zone.biomes[BIOME_NAMES[biome] as keyof typeof zone.biomes] ?? 0;
  return ubiome < weight ? { x, z } : undefined;
}
